'use client';

import { useState } from 'react';
import { Heart, Share2, MapPin, Calendar, Users, Music, ChevronRight } from 'lucide-react';
import Link from 'next/link';
import { useLocale, useTranslations } from 'next-intl';
import { useLocalizedField } from '@/hooks/useLocalizedField';

interface FestivalCardProps {
  item: {
    id: string;
    name: string;
    name_en?: string;
    description?: string;
    description_en?: string;
    cover?: string;
    images?: string[];
    destination?: {
      name: string;
      name_en?: string;
    };
    slug: string;
    [key: string]: any;
  };
}

const FestivalCard = ({ item }: FestivalCardProps) => {
  const locale = useLocale();
  const t = useTranslations();
  const { getLocalizedField } = useLocalizedField();
  const [isHovered, setIsHovered] = useState(false);
  const [isLiked, setIsLiked] = useState(false);
  const [imageError, setImageError] = useState(false);

  const fallbackImage = "https://images.unsplash.com/photo-1533174072545-7a4b6ad7a6c3?auto=format&fit=crop&w=800&q=80";
  const displayImage = imageError || !item.cover ? fallbackImage : item.cover;

  const localizedDescription = getLocalizedField(item, 'description');
  const truncatedDescription = localizedDescription
    ? localizedDescription.substring(0, 100) + (localizedDescription.length > 100 ? '...' : '')
    : t('festivals.noDescriptionAvailable');

  const handleLike = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsLiked(!isLiked);
  };

  const handleShare = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (navigator.share) {
      navigator.share({
        title: getLocalizedField(item, 'name'),
        text: getLocalizedField(item, 'description') || getLocalizedField(item, 'name'),
        url: window.location.href,
      });
    }
  };

  return (
    <Link href={`/festivals/${item.slug}`} className="block">
      <div
        className="
          relative
          bg-primary/20
          rounded-2xl overflow-hidden
          border border-gray-200 shadow-sm
          hover:shadow-xl
          transition-all duration-500
          h-[400px] w-full
          group
        "
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        dir={locale === 'ar' ? 'rtl' : 'ltr'}
      >
        {/* Main Image - Fixed height */}
        <div className="relative h-full w-full overflow-hidden">
          <img
            src={displayImage}
            alt={getLocalizedField(item, 'name')}
            className={`
              w-full h-full object-cover
              transition-all duration-700
              ${isHovered ? 'scale-110 brightness-90' : 'scale-100 brightness-100'}
            `}
            onError={() => setImageError(true)}
          />
          <div className="
                bg-black/30 
                text-white px-3 py-1.5
                 text-sm h-full
                w-full top-0 -left-0
                absolute
                flex items-center gap-2
                transition-all duration-300
                group-hover:bg-black/80
              "></div>
          
          {/* Gradient Overlay for better text visibility */}
          <div className={`
            absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent
            transition-opacity duration-500
            ${isHovered ? 'opacity-100' : 'opacity-80'}
          `} />
          
          {/* Title - Positioned above image (at the top) */}
          <div className={`
            absolute top-4 ${locale === 'ar' ? 'right-4 left-4' : 'left-4 right-4'}
            transition-all duration-500 ease-out
            ${isHovered ? 'opacity-100 translate-y-0' : 'opacity-100 translate-y-0'}
          `}>
            <h3 className="
              text-white text-xl font-bold
              drop-shadow-lg
              line-clamp-2
              transition-transform duration-500
              group-hover:translate-y-0
            ">
              {getLocalizedField(item, 'name')}
            </h3>
          </div>

          {/* Destination Badge - Bottom left */}
          {item.destination && (
            <div className={`
              absolute bottom-4 ${locale === 'ar' ? 'right-4' : 'left-4'}
              transition-all duration-500
              group-hover:-translate-y-12
            `}>
              <div className="
                bg-black/60 backdrop-blur-sm
                text-white px-3 py-1.5
                rounded-full text-sm
                flex items-center gap-2
                transition-all duration-300
                group-hover:bg-black/80
              ">
                <MapPin className="w-3.5 h-3.5" />
                <span>{getLocalizedField(item.destination, 'name')}</span>
              </div>
            </div>
          )}

        </div>

        {/* Content Panel - Slides up from bottom on hover */}
        <div className={`
          absolute bottom-0 left-0 right-0
          bg-gradient-to-t from-primary/30 text-white via-primary/20 to-transparent
          backdrop-blur-sm
          transform transition-all duration-700 ease-in-out
          ${isHovered ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'}
        `}>
          <div className="p-5 pt-8">
            {/* Description */}
            <p className="
              text-white text-sm mb-5
              line-clamp-3
              transition-all duration-500 delay-150
              group-hover:opacity-100
            ">
              {truncatedDescription}
            </p>

            {/* Festival Info Icons */}
            <div className="
              grid grid-cols-2 gap-3 mb-6
              transition-all duration-500 delay-200
              group-hover:opacity-100
            ">
              <div className="flex items-center gap-3">
                <div className="
                  p-2 bg-purple-100 rounded-lg
                  transition-all duration-300
                  text-white
                  group-hover:bg-purple-200 group-hover:scale-105
                ">
                  <Calendar className="w-4 h-4 text-purple-600" />
                </div>
                <span className="text-white text-sm font-medium">{t('festivals.annually')}</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="
                  p-2 bg-blue-100 rounded-lg
                  transition-all duration-300
                  group-hover:bg-blue-200 group-hover:scale-105
                ">
                  <Music className="w-4 h-4 text-blue-600" />
                </div>
                <span className="text-white text-sm font-medium">{t('festivals.multiGenre')}</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="
                  p-2 bg-green-100 rounded-lg
                  transition-all duration-300
                  group-hover:bg-green-200 group-hover:scale-105
                ">
                  <Users className="w-4 h-4 text-green-600" />
                </div>
                <span className="text-white text-sm font-medium">{t('festivals.international')}</span>
              </div>
            </div>

            {/* View Details Button */}
            <div className={`
              pt-4 border-t border-white/30
              transition-all duration-500 delay-300
              group-hover:opacity-100
              flex flex-row ${locale === 'ar' ? 'justify-start' : 'justify-end'}
            `}>
              <div className="
                flex items-center justify-between
                text-sm font-semibold
                text-white hover:text-purple-800
                transition-colors duration-200
              ">
                <span className="flex items-center gap-2">
                  {t('festivals.viewDetails')}
                  <ChevronRight className={`
                    w-4 h-4 transition-transform duration-300
                    ${isHovered ? (locale === 'ar' ? '-translate-x-1 rotate-180' : 'translate-x-1') : (locale === 'ar' ? 'rotate-180' : '')}
                  `} />
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
};

export default FestivalCard;